/**
 * @module utils/scrollIndicator
 *
 * The site's scrollbar, on every device: a thin thumb on the right edge, sized
 * and positioned from Lenis's own scroll progress.
 *
 * It started out as a touch-only stand-in, because Lenis (utils/lenis.js) runs
 * with `syncTouch` — it preventDefaults native touch scrolling and drives the
 * position itself, so mobile browsers never paint their overlay scrollbar. It now
 * covers desktop too, where the native bar was inconsistent by platform (a macOS
 * overlay that fades when idle vs a permanent Windows gutter). The native bar is
 * hidden to match — see components/scrollbar.css, which gates that on Lenis's own
 * `.lenis` root class so it only happens where this indicator is mounted.
 *
 * Visibility (the `.is-visible` class):
 *  - shown straight away on most pages;
 *  - on the homepage, held back until the hero intro finishes (`hero:ready`)
 *    AND the user starts scrolling — the intro auto-plays behind a body lock;
 *  - hidden whenever the page scroll is locked (nav open — see onScrollLockChange).
 *
 * Only mounted when Lenis is active, so it never runs under reduced motion —
 * where the native scrollbar is left in place instead.
 */

import { eventBus } from '@weareduet/plugins';
import { loadGsap } from '@/utils/gsap';
import { onScrollLockChange } from '@/utils/lenis';

const MIN_RATIO = 0.06; // floor on thumb height so it never shrinks to nothing

export function mountScrollIndicator(lenis) {
	if (!lenis) return () => {};

	const track = document.createElement('div');
	track.className = 'c-scrollbar';
	track.setAttribute('aria-hidden', 'true');

	const thumb = document.createElement('div');
	thumb.className = 'c-scrollbar__thumb';
	track.appendChild(thumb);
	document.body.appendChild(track);

	// Lenis emits { scroll, limit, velocity, progress, ... } every frame.
	function onScroll({ progress = 0, limit = 0 } = {}) {
		const viewport = window.innerHeight;
		// Thumb height mirrors the visible fraction of the document, like a native
		// scrollbar; `limit` is the max scrollable distance Lenis has measured.
		const ratio =
			limit > 0 ? Math.max(viewport / (viewport + limit), MIN_RATIO) : 1;
		thumb.style.height = `${ratio * 100}%`;
		thumb.style.top = `${progress * (1 - ratio) * 100}%`;
	}

	lenis.on('scroll', onScroll);

	// Draw from Lenis's current measurements — used for the first paint and after
	// any layout-changing refresh.
	const render = () =>
		onScroll({ progress: lenis.progress || 0, limit: lenis.limit || 0 });
	render();

	// Visibility. `revealed` = the page has decided the bar may show; `locked` =
	// scrolling is currently disabled. The thumb is visible only when both allow.
	let revealed = false;
	let locked = false;
	const applyVisibility = () =>
		track.classList.toggle('is-visible', revealed && !locked);
	const reveal = () => {
		revealed = true;
		applyVisibility();
	};

	// Hide while the page scroll is locked (nav open, etc.), restore on unlock.
	const offLock = onScrollLockChange(isLocked => {
		locked = isLocked;
		applyVisibility();
	});

	// Homepage: the hero intro auto-plays behind a body scroll-lock, so hold the
	// bar back until the intro finishes (hero:ready) AND the user scrolls.
	// Everywhere else there's no gated intro, so reveal immediately.
	const heroGated = !!document.querySelector(
		'[data-behaviour="HomeHeroCarousel"]'
	);
	let armed = false;
	const onHeroReady = () => {
		if (revealed || armed) return;
		armed = true;
		const onFirstScroll = () => {
			lenis.off('scroll', onFirstScroll);
			reveal();
		};
		lenis.on('scroll', onFirstScroll);
	};
	if (heroGated) eventBus.on('hero:ready', onHeroReady);
	else reveal();

	// The Approach page's pins grow the document height *after* mount, so the
	// seed above is measured against the pre-pin page. refreshScroll() fires a
	// ScrollTrigger refresh once the pins are built — re-measure on every refresh
	// (lenis.resize() recomputes its limit synchronously) so the always-visible
	// thumb is the right size from the first paint, not only after the first scroll.
	let destroyed = false;
	let removeRefresh = null;
	loadGsap().then(({ ScrollTrigger }) => {
		if (destroyed) return;
		const onRefresh = () => {
			lenis.resize();
			render();
		};
		ScrollTrigger.addEventListener('refresh', onRefresh);
		removeRefresh = () =>
			ScrollTrigger.removeEventListener('refresh', onRefresh);
		onRefresh();
	});

	return () => {
		destroyed = true;
		lenis.off('scroll', onScroll);
		offLock();
		eventBus.off('hero:ready', onHeroReady);
		if (removeRefresh) removeRefresh();
		track.remove();
	};
}
